import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICreateDestinatarioAsistencia,
  IDestinatarioAsistencia,
  IUpdateDestinatarioAsistencia,
} from 'modelos/src';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { DestinatariosAsistenciaService } from '../../../../../../auxiliares/servicios/http/destinatarios-asistencia.service';
import { LoginService } from '../../../../../login/login.service';
import { nombrePaciente, SEXOS } from '../../presentacion-entidades';

/**
 * Datos del diálogo. Para editar se pasa `idDestinatario` o `edit`; sin
 * ninguno de los dos, crea. `desdeEmergencia` (alta desde el formulario de
 * emergencia) oculta el selector de cliente.
 */
export interface ICrearEditarPacienteData {
  idDestinatario?: string;
  edit?: IDestinatarioAsistencia;
  desdeEmergencia?: boolean;
}

/**
 * Paciente (IDestinatarioAsistencia, siempre tipoEmergencia 'Médica'). Al
 * cerrarse como diálogo devuelve el paciente creado o actualizado.
 */
@Component({
  selector: 'app-crear-editar-destinatario-asistencia',
  imports: [AuxiliaresModule],
  templateUrl: './crear-editar-destinatario-asistencia.component.html',
  styleUrl: '../../../form-dialogo.scss',
})
export class CrearEditarDestinatarioAsistenciaComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(DestinatariosAsistenciaService);
  data = inject<ICrearEditarPacienteData | null>(MAT_DIALOG_DATA, {
    optional: true,
  });
  dialogRef =
    inject<MatDialogRef<CrearEditarDestinatarioAsistenciaComponent>>(
      MatDialogRef,
    );

  public id?: string | null;
  public loading = true;
  public guardando = false;
  public form?: FormGroup;
  public title = '';
  public subtitle?: string;
  public paciente?: IDestinatarioAsistencia;
  public sexos = SEXOS;
  public mostrarCliente =
    HelperService.getTipoCliente() !== 'Final' && !this.data?.desdeEmergencia;

  private createForm(): void {
    const p = this.paciente;
    this.form = new FormGroup({
      nombre: new FormControl(p?.nombre, Validators.required),
      apellido: new FormControl(p?.apellido),
      dni: new FormControl(p?.dni),
      sexo: new FormControl(p?.sexo),
      edad: new FormControl(p?.edad, [Validators.min(0), Validators.max(130)]),
      obraSocial: new FormControl(p?.obraSocial),
      idCliente: new FormControl(
        p?.idCliente ?? LoginService.getCliente()?._id,
      ),
      telefono: new FormControl(p?.telefono),
      telefonoAlternativo: new FormControl(p?.telefonoAlternativo),
      email: new FormControl(p?.email, Validators.email),
      descripcion: new FormControl(p?.infoAdicional?.descripcion),
    });
  }

  private getData():
    | ICreateDestinatarioAsistencia
    | IUpdateDestinatarioAsistencia {
    const v = this.form!.value;
    // Al editar, un campo vaciado se manda vacío para que se borre; al crear
    // directamente no se manda.
    const texto = (x?: string | null) =>
      x?.trim() || (this.id ? '' : undefined);
    const sinEdad = v.edad === null || v.edad === undefined || v.edad === '';
    const data: ICreateDestinatarioAsistencia = {
      nombre: v.nombre?.trim(),
      apellido: texto(v.apellido),
      dni: texto(v.dni),
      sexo: v.sexo || undefined,
      edad: sinEdad
        ? this.id
          ? (null as unknown as number)
          : undefined
        : Number(v.edad),
      obraSocial: texto(v.obraSocial),
      telefono: texto(v.telefono),
      telefonoAlternativo: texto(v.telefonoAlternativo),
      email: texto(v.email),
      infoAdicional: {
        ...this.paciente?.infoAdicional,
        descripcion: texto(v.descripcion),
      },
      tipoEmergencia: 'Médica',
    };
    if (!this.id && this.mostrarCliente && v.idCliente) {
      data.idCliente = v.idCliente;
    }
    return data;
  }

  public async onSubmit(): Promise<void> {
    if (!this.form?.valid) return;
    this.guardando = true;
    try {
      let resultado: IDestinatarioAsistencia;
      if (this.id) {
        resultado = await this.service.update(this.id, this.getData());
        this.helper.notifSuccess('Paciente actualizado correctamente');
      } else {
        resultado = await this.service.create(this.getData());
        this.helper.notifSuccess('Paciente creado correctamente');
      }
      this.close(resultado);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  public close(paciente?: IDestinatarioAsistencia): void {
    this.dialogRef.close(paciente);
  }

  async ngOnInit(): Promise<void> {
    try {
      this.id = this.data?.idDestinatario || this.data?.edit?._id || null;
      if (this.id) {
        this.paciente = await this.service.getById(this.id);
      }
      this.title = this.id ? 'Editar paciente' : 'Crear paciente';
      this.subtitle = nombrePaciente(this.paciente) || undefined;
      this.createForm();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }
}
